import { useEffect, useRef, useState } from "react";
import { Box, Button, List, Skeleton } from "@mui/material";
import { MDXEditor, MDXEditorMethods, } from "@mdxeditor/editor";
import { useRootStore } from "../hooks/useRootStore";
import './IssueActivityList.css'
import { observer } from "mobx-react-lite";
import IssueActivity from "./IssueActivity";
import { IssueActivity as IssueActivityType } from "../data/IssueActivity";
import { ActivityType } from "../data/ActivityType";
import { MdxPlugins } from "./MarkdownEditor/MarkdownEditorPlugins";
import { Timesheet } from "../data/Timesheet";
import useMediaQuery from '@mui/material/useMediaQuery';
import { IssueComment } from "../data/IssueComment";

const IssueActivityList = observer(({ issueId }: { issueId: number }) => {

    const mobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const [loading, setLoading] = useState(true);
    const [activities, setActivities] = useState([] as IssueActivityType[]);
    const { api, userProfile, timesheetsChannel, issuesChannel } = useRootStore();

    const [editorLoaded, setEditorLoaded] = useState(false);

    useEffect(() => {
        api.getAcivities(issueId).then(activities => {
            setActivities(activities);
            setLoading(false);
        })
    }, [api, issueId]);

    const mdxEditorRef = useRef<MDXEditorMethods>(null);

    const handleNewComment = () => {
        const newComment = mdxEditorRef.current?.getMarkdown() ?? '';
        if (newComment != '') {
            api.postIssueComment(issueId, newComment, false).then(newCommentId => {
                if (newCommentId != 0) {
                    mdxEditorRef.current?.setMarkdown('');
                    setActivities([...activities, {
                        activityType: ActivityType.comment,
                        data: {
                            ID: newCommentId,
                            IssueID: issueId,
                            AuthorID: userProfile!.ID,
                            AuthorName: `${userProfile!.LastName} ${userProfile!.FirstName}`,
                            CommentText: newComment,
                            CreateDate: new Date(),
                            Hidden: false,
                        } as IssueComment
                    }]);
                }
            });
        }
    };

    useEffect(() => {
        const unsubscribeOnTimesheetAdded = timesheetsChannel.on('onTimesheetAdded', (timesheet: Timesheet) => {
            if (timesheet.IssueID == issueId) {
                setActivities([...activities, {
                    activityType: ActivityType.timesheetRecord,
                    data: timesheet
                }]);
            }
        });

        const unsubscribeOnIssueChanged = issuesChannel.on('onIssueChanged', (changedIssueId: number) => {
            if (changedIssueId == issueId) {
                const comments = activities.filter(x => x.activityType == ActivityType.comment);
                const lastCommentID = comments.length > 0 ? comments[comments.length-1].data.ID : 0;
                api.getIssueComments(changedIssueId, lastCommentID).then(newComments => {
                    const cs = [...activities];
                    newComments.forEach(comment => {
                        cs.push({ activityType: ActivityType.comment, data: comment })
                    })
                    setActivities(cs);
                });
            }
        });

        return () => {
            unsubscribeOnTimesheetAdded();
            unsubscribeOnIssueChanged();
        }
    }, [issueId, activities, timesheetsChannel])

    useEffect(() => {
        if (editorLoaded) {
            const mdxEditor = document.getElementsByClassName('markdown-editor-with-ctrl-enter')[0];

            const handleCtrlEnter = function (event: Event) {
                if ((event as KeyboardEvent).ctrlKey && (event as KeyboardEvent).key === 'Enter') {
                    event.preventDefault();
                    handleNewComment();
                }
            };

            if (mdxEditor) {
                mdxEditor!.addEventListener('keydown', handleCtrlEnter);
            }

            return () => {
                if (mdxEditor) {
                    mdxEditor!.removeEventListener('keydown', handleCtrlEnter);
                }
            }
        }
    }, [editorLoaded]);

    return (
        <Box className="issue-activity">
            <Box sx={{ fontWeight: 'bold', fontSize: '1.25em', mt: 2 }}>Активность</Box>
            {
                loading
                    ?
                    <>
                        <Box sx={{ display: 'flex', mt: 3}}>
                            <Skeleton variant="circular" width='40px' height='40px' sx={{mr:2}} />
                            <Skeleton variant="rounded" width='65%' height={120} />
                        </Box>
                        <Box sx={{ display: 'flex', mt: 3, justifyContent: 'flex-end' }}>
                            <Skeleton variant="circular" width='40px' height='40px' sx={{mr:2}} />
                            <Skeleton variant="rounded" width='80%' height={120} />
                        </Box>
                        <Box sx={{ display: 'flex', mt: 3}}>
                            <Skeleton variant="circular" width='40px' height='40px' sx={{mr:2}} />
                            <Skeleton variant="rounded" width='55%' height={120} />
                        </Box>
                    </>
                    :
                    <List className="issue-activities">
                        {activities.length === 0 ? 'Нет активностей' : activities.map(a => <IssueActivity key={ActivityType[a.activityType] + a.data.ID} activity={a} editor={mdxEditorRef} />)}
                        <Box sx={{ mt: 2 }}>Оставить комментарий:</Box>
                        <MDXEditor
                            ref={mdxEditorRef}                            
                            markdown=""
                            onChange={() => {
                                if (!editorLoaded) setEditorLoaded(true);
                            } }
                            contentEditableClassName="markdown-editor-with-ctrl-enter"
                            plugins={MdxPlugins.standardPlugins(api, issueId, '', mobile)}
                        //onKeyDown={(e) => { if (e.ctrlKey && e.key === 'Enter') handleNewComment() }}
                        />
                        <Button style={{ marginTop: '10px' }} onClick={handleNewComment}>Комментировать</Button>
                    </List>
            }
        </Box>
    )
})

export default IssueActivityList;